import { initLazyImages } from '../utils/lazy-images.js';
import { debounce } from '../utils/utils.js';

/**
 * Represents a single filter form component that fetches and updates
 * results based on user input.
 */
class FilterForm extends HTMLElement {
    /** @type {HTMLFormElement | null} */
    form = null;
    /** @type {HTMLElement | null} */
    results = null;
    /** @type {HTMLElement | null} */
    productCount = null;
    /** @type {NodeListOf<Element>} */
    showIfFilterElements = [];
    /** @type {NodeListOf<Element>} */
    selectedCountElements = [];
    /** @type {HTMLElement & { reload?: () => void, dataset: DOMStringMap } | null} */
    infiniteCollection = null;
    /** @type {Function} */
    debouncedApplyFilterChange;
    /** @type {(event: PopStateEvent) => void} */
    boundHandleHistoryChange;
    /** @type {(event: CustomEvent) => void} */
    boundHandleRemoveFilter;

    connectedCallback() {
        this.init();
    }

    disconnectedCallback() {
        window.removeEventListener('popstate', this.boundHandleHistoryChange);
        document.removeEventListener('remove-filter', this.boundHandleRemoveFilter);
    }

    /**
     * Initializes the component, caches elements, and sets up listeners.
     */
    init() {
        this.form = this.querySelector('form');
        if (!this.form) {
            console.error('FilterForm: No form element found within the component.');
            return;
        }

        this.results = document.querySelector('.js-filter-results');
        this.productCount = document.getElementById('product-count');
        this.infiniteCollection = document.querySelector('infinite-collection');

        this.showIfFilterElements = document.querySelectorAll('[data-show-if-filters]');
        this.selectedCountElements = document.querySelectorAll('[data-filters-selected]');

        if (!this.results) {
            console.warn('FilterForm: Results container (.js-filter-results) not found.');
        }

        this.debouncedApplyFilterChange = debounce(this.applyFilterChange.bind(this), 500);
        this.boundHandleHistoryChange = this.handleHistoryChange.bind(this);
        this.boundHandleRemoveFilter = this.handleRemoveFilter.bind(this);

        this.addListeners();

        const initialSearchParams = window.location.search.substring(1);
        this.updateUI(initialSearchParams);
        this.updateSelectedCount();
    }

    /**
     * Adds event listeners to the form and window.
     */
    addListeners() {
        this.form?.addEventListener('input', this.handleFormInput.bind(this));

        this.form?.addEventListener('click', this.handleClick.bind(this));
        window.addEventListener('popstate', this.boundHandleHistoryChange);

        // Listen for custom remove-filter events
        document.addEventListener('remove-filter', this.boundHandleRemoveFilter);
    }

    /**
     * Handles 'click' events, specifically for clearing filter sections.
     * @param {MouseEvent} event
     */
    handleClick(event) {
        const clearButton = event.target.closest('[data-clear-filter]');

        if (clearButton && this.form) {
            event.preventDefault();
            const details = clearButton.closest('details');
            if (!details) return;

            const inputsToClear = details.querySelectorAll('input[type="checkbox"], input[type="radio"]');
            inputsToClear.forEach((input) => {
                if (input instanceof HTMLInputElement) input.checked = false;
            });

            const rangesToReset = details.querySelectorAll('input[type="range"]');
            rangesToReset.forEach((range) => {
                if (range instanceof HTMLInputElement) {
                    range.value = range.min || '0';

                    range.dispatchEvent(new Event('input', { bubbles: true }));
                }
            });

            this.handleFormInput();
        }
    }

    /**
     * Handles custom 'remove-filter' events to remove specific filter values.
     * @param {CustomEvent} event - The custom event containing filter details.
     */
    handleRemoveFilter(event) {
        const { filterKey, filterValue } = event.detail;

        if (!filterKey || !filterValue) {
            console.error('Invalid remove-filter event data:', event.detail);
            return;
        }

        const currentParams = new URLSearchParams(window.location.search);

        const currentValues = currentParams.getAll(filterKey);
        currentParams.delete(filterKey);

        currentValues.forEach((value) => {
            if (value !== filterValue) {
                currentParams.append(filterKey, value);
            }
        });

        this.applyFilters(currentParams.toString());
    }

    /**
     * Handles 'input' events on the form. Updates counts and debounces filter application.
     * @param {Event} event - The input event object (optional, can be called directly).
     */
    handleFormInput() {
        this.updateSelectedCount();
        this.debouncedApplyFilterChange();
    }

    /**
     * Gathers form data, prepares search params, and triggers filter application.
     */
    applyFilterChange() {
        if (!this.form) return;

        const formData = new FormData(this.form);
        const searchParams = new URLSearchParams();

        for (const [key, value] of formData.entries()) {
            if (value === '') continue;

            if (key === 'filter.p.m.custom.number_of_people' && value === '0') continue;

            searchParams.append(key, value);
        }

        this.applyFilters(searchParams.toString());
    }

    /**
     * Handles browser history changes (back/forward buttons).
     * @param {PopStateEvent} event - The popstate event object.
     */
    handleHistoryChange(event) {
        const searchParams = event.state?.searchParams ?? window.location.search.substring(1);

        this.applyFilters(searchParams, false);

        this.syncFormInputs(searchParams);
    }

    /**
     * Updates the form's input elements to match the provided search parameters.
     * Needed after back/forward navigation.
     * @param {string} searchParamsString - The query string.
     */
    syncFormInputs(searchParamsString) {
        if (!this.form) return;
        const params = new URLSearchParams(searchParamsString);

        this.form.querySelectorAll('input[type="checkbox"], input[type="radio"]').forEach((input) => {
            if (input instanceof HTMLInputElement) input.checked = false;
        });
        this.form
            .querySelectorAll('input:not([type="checkbox"]):not([type="radio"]), select, textarea')
            .forEach((input) => {
                if (
                    input instanceof HTMLInputElement ||
                    input instanceof HTMLSelectElement ||
                    input instanceof HTMLTextAreaElement
                ) {
                    if (input.type === 'range' && input instanceof HTMLInputElement) {
                        input.value = input.min || '0';
                    } else {
                        input.value = '';
                    }
                }
            });

        for (const [key, value] of params.entries()) {
            const inputs = this.form.querySelectorAll(`[name="${key}"]`);
            inputs.forEach((input) => {
                if (
                    input instanceof HTMLInputElement &&
                    (input.type === 'checkbox' || input.type === 'radio')
                ) {
                    if (input.value === value) {
                        input.checked = true;
                    }
                } else if (
                    input instanceof HTMLInputElement ||
                    input instanceof HTMLSelectElement ||
                    input instanceof HTMLTextAreaElement
                ) {
                    input.value = value;

                    if (input.type === 'range') {
                        input.dispatchEvent(new Event('input', { bubbles: true }));
                    }
                }
            });
        }

        this.updateSelectedCount();
        this.updateUI(searchParamsString);
    }

    /**
     * Fetches filtered results and updates the DOM.
     * @param {string} searchParams - The filter/sort query string.
     * @param {boolean} [updateUrl=true] - Whether to update the browser URL/history.
     */
    async applyFilters(searchParams, updateUrl = true) {
        if (!this.results) return;

        if (this.infiniteCollection) this.infiniteCollection.dataset.pauseInfiniteScroll = 'true';

        this.results.classList.add('is-loading');
        this.updateUI(searchParams);

        try {
            const fetchUrl = `${window.location.pathname}?${searchParams}`;
            const response = await fetch(fetchUrl);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const html = await response.text();
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');

            const newProductCount = doc.getElementById('product-count');
            const newResults = doc.querySelector('.js-filter-results');

            if (this.productCount && newProductCount) {
                this.productCount.innerHTML = newProductCount.innerHTML;
            }
            if (newResults) {
                this.results.innerHTML = newResults.innerHTML;
            } else {
                this.results.innerHTML = '<p>No results found.</p>';
            }

            initLazyImages();

            if (this.infiniteCollection?.reload) {
                this.infiniteCollection.reload();
            }

            if (updateUrl) {
                FilterForm.updateURL(searchParams);
            }

            if (this.infiniteCollection) this.infiniteCollection.dataset.pauseInfiniteScroll = 'false';

            this.dispatchEvent(new CustomEvent('on:filters:updated', { bubbles: true }));
        } catch (error) {
            console.error('Error fetching or applying filters:', error);

            this.results.innerHTML = '<p>Sorry, there was an error loading results.</p>';
        } finally {
            this.results.classList.remove('is-loading');
        }
    }

    /**
     * Updates the browser's URL and history state.
     * @param {string} searchParams - The query string.
     */
    static updateURL(searchParams) {
        const url = `${window.location.pathname}${searchParams ? `?${searchParams}` : ''}`;
        window.history.pushState({ searchParams }, '', url);
    }

    /**
     * Updates UI elements based on whether any filters are active.
     * Uses cached element list.
     * @param {string} searchParams - The query string.
     */
    updateUI(searchParams) {
        const hasFilters = searchParams.length > 0;
        this.showIfFilterElements.forEach((el) => el.classList.toggle('hidden', !hasFilters));
    }

    /**
     * Updates the display of how many filters are selected within each details/filter group.
     * Uses cached element list.
     */
    updateSelectedCount() {
        this.selectedCountElements.forEach((countEl) => {
            const detailsEl = countEl.closest('details');
            if (!detailsEl) return;

            const inputs = detailsEl.querySelectorAll(
                'input[type="checkbox"]:checked, input[type="radio"]:checked'
            );
            countEl.textContent = inputs.length.toString();

            const summary = detailsEl.querySelector('summary');
            if (summary) summary.classList.toggle('has-active-filters', inputs.length > 0);
        });
    }
}

class RemoveFilterButton extends HTMLElement {
    connectedCallback() {
        this.addEventListener('click', this.handleClick.bind(this));
    }

    handleClick() {
        const filterKey = this.dataset.filterKey;
        const filterValue = this.dataset.filterValue;

        document.dispatchEvent(
            new CustomEvent('remove-filter', {
                detail: {
                    filterKey,
                    filterValue
                }
            })
        );
    }
}

customElements.define('filter-form', FilterForm);
customElements.define('remove-filter-button', RemoveFilterButton);
